import { getAccountIdFromPath } from './get-account-id-from-path';

/**
 * Brandbot paths that are never account-scoped (auth / onboarding / infra).
 * Mirrors `ACCOUNT_AGNOSTIC_PATHS` in brandbot-frontend, matched by first path
 * segment; an empty segment is the bare root (`/`).
 */
const ACCOUNT_AGNOSTIC_FIRST_SEGMENTS = new Set([
  '',
  'legacy-login',
  'login',
  'callback',
  'bridge-callback',
  'logout',
  'browser',
  'signup',
  'forgot-password',
  'reset-password',
  'confirm-needed',
  'confirm',
  'health',
  'select-account',
  'default',
  'no-account-access',
]);

const ACCOUNT_PREFIXED_PATH_RE = /^\/\d+(\/|$)/;

/**
 * Data attribute holding the nav link's original href. The rendered `href` may
 * be rewritten with the URL account, so code that matches links to services
 * must read this instead.
 */
export const SERVICE_HREF_DATA_KEY = 'ghServiceHref';

export function getServiceHref(linkElm: HTMLElement | null | undefined) {
  return (
    linkElm?.dataset?.[SERVICE_HREF_DATA_KEY] ??
    linkElm?.getAttribute('href') ??
    null
  );
}

/**
 * With multi-account on, Brandbot scopes each tab to the account in its URL
 * (`/{account_id}/…`). Nav service URLs are flat (`/contacts`), and a flat URL
 * opened outside the SPA router (new tab/window, middle/cmd click, context
 * menu) cold-loads under the session account instead of the tab's account.
 * Prefix such hrefs with the current URL account so every way of opening the
 * link lands on the same account.
 *
 * Returned unchanged when: the flag is off; the page URL has no account id;
 * the href is not http(s), is unparsable, or points to a host other than the
 * page / Brandbot host; the path is already account-prefixed; or the path is
 * account-agnostic. The target origin is always preserved.
 */
export function toAccountScopedHref(
  href: string | null,
  {
    multiAccountEnabled,
    brandbotBaseUrl,
  }: { multiAccountEnabled: boolean; brandbotBaseUrl?: string },
): string | null {
  if (!href || !multiAccountEnabled) return href;

  const accountId = getAccountIdFromPath(window.location.pathname);
  if (!accountId) return href;

  let target: URL;
  try {
    target = new URL(href, window.location.href);
  } catch {
    return href;
  }

  if (target.protocol !== 'http:' && target.protocol !== 'https:') return href;

  const allowedOrigins = [window.location.origin];
  if (brandbotBaseUrl) {
    try {
      allowedOrigins.push(new URL(brandbotBaseUrl).origin);
    } catch {
      // Misconfigured base URL; only the page origin is trusted.
    }
  }
  if (!allowedOrigins.includes(target.origin)) return href;

  if (ACCOUNT_PREFIXED_PATH_RE.test(target.pathname)) return href;

  const firstSegment = target.pathname.split('/')[1] ?? '';
  if (ACCOUNT_AGNOSTIC_FIRST_SEGMENTS.has(firstSegment)) return href;

  return `${target.origin}/${accountId}${target.pathname}${target.search}${target.hash}`;
}
